import { useEffect, useRef } from "react";

import "./ConvertFileToImageType.scss";

export interface ConvertFileToImageTypeProps {
  maxImageWidth: number;
  maxImageHeight: number;
  selectedFile: File;
  targetImageType: string;
  onConversion: (blob: Blob) => void;
}

export function computeResizedWidthHeight(width, height, maxWidth, maxHeight) {
  if (width <= maxWidth && height <= maxHeight) {
    return {
      resizedWidth: width,
      resizedHeight: height,
      resize: false
    };
  }

  // figure out which is more excessive, width or height, and shrink that, keeping apect ratio ;fixed
  if (width / maxWidth > height / maxHeight) {
    return {
      resizedWidth: maxWidth,
      resizedHeight: Math.round((height * maxWidth) / width),
      resize: true
    };
  } else {
    return {
      resizedWidth: Math.round((width * maxHeight) / height),
      resizedHeight: maxHeight,
      resize: true
    };
  }
}

let lastSelectedFile: File | null = null;

export default function ConvertFileToImageType({
  maxImageWidth,
  maxImageHeight,
  selectedFile,
  targetImageType,
  onConversion
}: ConvertFileToImageTypeProps) {
  const canvasRef = useRef(null);
  const processedCanvasRef = useRef(null);

  const defaultConvertImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const uploadedCanvas = canvasRef.current as HTMLCanvasElement | null;
      const processedCanvas =
        processedCanvasRef.current as HTMLCanvasElement | null;

      if (!uploadedCanvas || !processedCanvas) {
        reject(new Error("Canvas elements not found"));
        return;
      }

      const uploadedCtx = uploadedCanvas.getContext("2d");
      const processedCtx = processedCanvas.getContext("2d");

      if (!uploadedCtx || !processedCtx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      const img = new Image();

      img.onload = () => {
        try {
          const { width, height } = img;
          const { resizedWidth, resizedHeight, resize } =
            computeResizedWidthHeight(
              width,
              height,
              maxImageWidth,
              maxImageHeight
            );

          if (resize) {
            console.log(
              `converting ${width}x${height} image to ${resizedWidth}x${resizedHeight} ${targetImageType}`
            );
          }

          uploadedCanvas.width = width;
          uploadedCanvas.height = height;
          processedCanvas.width = resizedWidth;
          processedCanvas.height = resizedHeight;

          uploadedCtx.drawImage(img, 0, 0);

          processedCtx.clearRect(0, 0, resizedWidth, resizedHeight);
          processedCtx.drawImage(img, 0, 0, resizedWidth, resizedHeight);

          // Convert processed canvas to blob with correct type
          processedCanvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(
                  new Error(`Failed to convert image to ${targetImageType}`)
                );
              }
            },
            `image/${targetImageType === "jpg" ? "jpeg" : targetImageType}`,
            1.0 // Quality (0-1, where 1 is highest quality)
          );
        } catch (error) {
          reject(
            new Error(
              `Image processing failed: ${error instanceof Error ? error.message : "Unknown error"}`
            )
          );
        } finally {
          // Clean up object URL
          URL.revokeObjectURL(img.src);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error("Failed to load image"));
      };

      // Create object URL from file and load it
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  };

  const convertImage = defaultConvertImage;

  useEffect(() => {
    const doConvert = async (file: File) => {
      try {
        const blob = await convertImage(file);
        onConversion(blob);
      } catch (error) {
        console.error("Error converting image:", error);
      }
    };
    if (selectedFile && lastSelectedFile !== selectedFile) {
      lastSelectedFile = selectedFile;
      doConvert(selectedFile);
    }
  }, [selectedFile]);

  return (
    <div className="canvas-box">
      <canvas ref={canvasRef} />
      <canvas ref={processedCanvasRef} />
    </div>
  );
}
